import {
  ContextType,
  PreferenceScope
} from "./chunk-VYSGOHAZ.js";

// src/transport/graphql-adapter.ts
var BOOTSTRAP_QUERY = `
  query BootstrapPreferences($domains: [String!], $includeProvenance: Boolean) {
    bootstrapPreferences(domains: $domains, includeProvenance: $includeProvenance) {
      documents {
        domain
        contextType
        contextKey
        documentFamily
        scope
        version
        revision
        data
        updatedAt
      }
      effectiveDocuments {
        domain
        contextType
        contextKey
        documentFamily
        version
        data
        provenance
      }
    }
  }
`.trim();
var PREFERENCE_DOCUMENT_QUERY = `
  query PreferenceDocument(
    $domain: String!
    $contextType: String!
    $contextKey: String!
    $documentFamily: String!
    $scope: String
  ) {
    preferenceDocument(
      domain: $domain
      contextType: $contextType
      contextKey: $contextKey
      documentFamily: $documentFamily
      scope: $scope
    ) {
      domain
      contextType
      contextKey
      documentFamily
      scope
      version
      revision
      data
      updatedAt
    }
  }
`.trim();
var EFFECTIVE_PREFERENCES_QUERY = `
  query EffectivePreferences(
    $domain: String!
    $contextType: String!
    $contextKey: String!
    $documentFamily: String!
    $includeProvenance: Boolean
  ) {
    effectivePreferences(
      domain: $domain
      contextType: $contextType
      contextKey: $contextKey
      documentFamily: $documentFamily
      includeProvenance: $includeProvenance
    ) {
      domain
      contextType
      contextKey
      documentFamily
      version
      data
      provenance
    }
  }
`.trim();
var PATCH_PREFERENCE_DOCUMENT_MUTATION = `
  mutation PatchPreferenceDocument(
    $domain: String!
    $contextType: String!
    $contextKey: String!
    $documentFamily: String!
    $scope: String!
    $data: String!
    $expectedRevision: String
  ) {
    patchPreferenceDocument(
      domain: $domain
      contextType: $contextType
      contextKey: $contextKey
      documentFamily: $documentFamily
      scope: $scope
      data: $data
      expectedRevision: $expectedRevision
    ) {
      kind
      document {
        domain
        contextType
        contextKey
        documentFamily
        scope
        version
        revision
        data
        updatedAt
      }
      effectiveData
      newRevision
      expectedRevision
      actualRevision
      serverData
      errors {
        path
        code
        message
      }
    }
  }
`.trim();
var RESET_PREFERENCE_DOCUMENT_MUTATION = `
  mutation ResetPreferenceDocument(
    $domain: String!
    $contextType: String!
    $contextKey: String!
    $documentFamily: String!
    $scope: String!
  ) {
    resetPreferenceDocument(
      domain: $domain
      contextType: $contextType
      contextKey: $contextKey
      documentFamily: $documentFamily
      scope: $scope
    ) {
      kind
      document {
        domain
        contextType
        contextKey
        documentFamily
        scope
        version
        revision
        data
        updatedAt
      }
      effectiveData
      newRevision
      expectedRevision
      actualRevision
      serverData
      errors {
        path
        code
        message
      }
    }
  }
`.trim();
var PREFERENCE_EVENTS_SUBSCRIPTION = `
  subscription PreferenceEvents(
    $domain: String!
    $contextType: String
    $contextKey: String
  ) {
    preferenceChanged(
      domain: $domain
      contextType: $contextType
      contextKey: $contextKey
    ) {
      domain
      contextType
      contextKey
      documentFamily
      scope
      newRevision
      timestamp
    }
  }
`.trim();
function parseContextType(raw) {
  const valid = Object.values(ContextType);
  if (!valid.includes(raw)) {
    throw new Error(
      `GraphQLTransportAdapter: server returned unknown contextType "${raw}". Expected one of: ${valid.join(", ")}.`
    );
  }
  return raw;
}
function parsePreferenceScope(raw) {
  const valid = Object.values(PreferenceScope);
  if (!valid.includes(raw)) {
    throw new Error(
      `GraphQLTransportAdapter: server returned unknown scope "${raw}". Expected one of: ${valid.join(", ")}.`
    );
  }
  return raw;
}
function parseJsonField(raw, fieldName) {
  try {
    const parsed = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new TypeError(`Expected a JSON object, got ${JSON.stringify(parsed)}`);
    }
    return parsed;
  } catch (cause) {
    throw new Error(
      `GraphQLTransportAdapter: failed to parse "${fieldName}" as JSON. Cause: ${cause instanceof Error ? cause.message : String(cause)}`
    );
  }
}
function buildIdentityFromDTO(dto) {
  return {
    domain: dto.domain,
    contextType: parseContextType(dto.contextType),
    contextKey: dto.contextKey,
    documentFamily: dto.documentFamily
  };
}
function mapDocumentFromDTO(dto) {
  return {
    identity: buildIdentityFromDTO(dto),
    scope: parsePreferenceScope(dto.scope),
    version: dto.version,
    revision: dto.revision,
    data: parseJsonField(dto.data, "document.data"),
    updatedAt: dto.updatedAt
  };
}
function mapResolvedFromDTO(dto) {
  const provenance = {};
  for (const [field, rawScope] of Object.entries(dto.provenance)) {
    provenance[field] = parsePreferenceScope(rawScope);
  }
  const scopeOrder = [
    "SYSTEM_DEFAULT" /* SYSTEM_DEFAULT */,
    "TENANT_DEFAULT" /* TENANT_DEFAULT */,
    "ROLE_DEFAULT" /* ROLE_DEFAULT */,
    "USER_OVERRIDE" /* USER_OVERRIDE */
  ];
  const provenanceScopes = new Set(Object.values(provenance));
  const layers = scopeOrder.filter((s) => provenanceScopes.has(s));
  return {
    identity: buildIdentityFromDTO(dto),
    version: dto.version,
    data: parseJsonField(dto.data, "resolved.data"),
    provenance,
    layers
  };
}
function mapMutationResultFromDTO(dto) {
  switch (dto.kind) {
    case "success": {
      if (dto.document === void 0) {
        throw new Error(
          'GraphQLTransportAdapter: mutation result kind is "success" but "document" field is absent.'
        );
      }
      if (dto.effectiveData === void 0) {
        throw new Error(
          'GraphQLTransportAdapter: mutation result kind is "success" but "effectiveData" field is absent.'
        );
      }
      if (dto.newRevision === void 0) {
        throw new Error(
          'GraphQLTransportAdapter: mutation result kind is "success" but "newRevision" field is absent.'
        );
      }
      const success = {
        kind: "success",
        document: mapDocumentFromDTO(dto.document),
        effectiveData: parseJsonField(dto.effectiveData, "mutationResult.effectiveData"),
        newRevision: dto.newRevision
      };
      return success;
    }
    case "conflict": {
      if (dto.expectedRevision === void 0) {
        throw new Error(
          'GraphQLTransportAdapter: mutation result kind is "conflict" but "expectedRevision" field is absent.'
        );
      }
      if (dto.actualRevision === void 0) {
        throw new Error(
          'GraphQLTransportAdapter: mutation result kind is "conflict" but "actualRevision" field is absent.'
        );
      }
      if (dto.serverData === void 0) {
        throw new Error(
          'GraphQLTransportAdapter: mutation result kind is "conflict" but "serverData" field is absent.'
        );
      }
      const conflict = {
        kind: "conflict",
        expectedRevision: dto.expectedRevision,
        actualRevision: dto.actualRevision,
        serverData: parseJsonField(dto.serverData, "mutationResult.serverData")
      };
      return conflict;
    }
    case "validation_error": {
      if (!Array.isArray(dto.errors) || dto.errors.length === 0) {
        throw new Error(
          'GraphQLTransportAdapter: mutation result kind is "validation_error" but "errors" array is absent or empty.'
        );
      }
      const validationError = {
        kind: "validation_error",
        errors: dto.errors.map((e) => ({
          path: e.path,
          code: e.code,
          message: e.message
        }))
      };
      return validationError;
    }
    default: {
      const exhausted = dto.kind;
      throw new Error(
        `GraphQLTransportAdapter: unrecognised mutation result kind "${String(exhausted)}".`
      );
    }
  }
}
function unwrapResponse(response, operationName) {
  if (response.errors !== void 0 && response.errors.length > 0) {
    const messages = response.errors.map((e) => e.message).join("; ");
    throw new Error(
      `GraphQLTransportAdapter: operation "${operationName}" returned GraphQL errors: ${messages}`
    );
  }
  if (response.data === void 0 || response.data === null) {
    throw new Error(
      `GraphQLTransportAdapter: operation "${operationName}" returned no data and no errors.`
    );
  }
  return response.data;
}
var GraphQLTransportAdapter = class {
  #executor;
  constructor(executor) {
    this.#executor = executor;
  }
  // -------------------------------------------------------------------------
  // TransportAdapter — queries
  // -------------------------------------------------------------------------
  async bootstrap(input) {
    const variables = {
      domains: input.domains !== void 0 ? [...input.domains] : void 0,
      includeProvenance: input.includeProvenance
    };
    const raw = await this.#executor({
      query: BOOTSTRAP_QUERY,
      variables
    });
    const data = unwrapResponse(raw, "bootstrapPreferences");
    return {
      documents: data.bootstrapPreferences.documents.map((dto) => mapDocumentFromDTO(dto)),
      effectiveDocuments: data.bootstrapPreferences.effectiveDocuments.map(
        (dto) => mapResolvedFromDTO(dto)
      )
    };
  }
  async fetchDocument(input) {
    const variables = {
      domain: input.identity.domain,
      contextType: input.identity.contextType,
      contextKey: input.identity.contextKey,
      documentFamily: input.identity.documentFamily,
      scope: input.scope
    };
    const raw = await this.#executor({
      query: PREFERENCE_DOCUMENT_QUERY,
      variables
    });
    const data = unwrapResponse(raw, "preferenceDocument");
    if (data.preferenceDocument === null) {
      return null;
    }
    return mapDocumentFromDTO(data.preferenceDocument);
  }
  async fetchEffective(input) {
    const variables = {
      domain: input.identity.domain,
      contextType: input.identity.contextType,
      contextKey: input.identity.contextKey,
      documentFamily: input.identity.documentFamily,
      includeProvenance: input.includeProvenance
    };
    const raw = await this.#executor({
      query: EFFECTIVE_PREFERENCES_QUERY,
      variables
    });
    const data = unwrapResponse(raw, "effectivePreferences");
    if (data.effectivePreferences === null) {
      return null;
    }
    return mapResolvedFromDTO(data.effectivePreferences);
  }
  // -------------------------------------------------------------------------
  // TransportAdapter — mutations
  // -------------------------------------------------------------------------
  async patch(input) {
    const variables = {
      domain: input.identity.domain,
      contextType: input.identity.contextType,
      contextKey: input.identity.contextKey,
      documentFamily: input.identity.documentFamily,
      scope: input.scope,
      data: JSON.stringify(input.data),
      expectedRevision: input.expectedRevision
    };
    const raw = await this.#executor({
      query: PATCH_PREFERENCE_DOCUMENT_MUTATION,
      variables
    });
    const data = unwrapResponse(raw, "patchPreferenceDocument");
    return mapMutationResultFromDTO(data.patchPreferenceDocument);
  }
  async reset(input) {
    const variables = {
      domain: input.identity.domain,
      contextType: input.identity.contextType,
      contextKey: input.identity.contextKey,
      documentFamily: input.identity.documentFamily,
      scope: input.scope
    };
    const raw = await this.#executor({
      query: RESET_PREFERENCE_DOCUMENT_MUTATION,
      variables
    });
    const data = unwrapResponse(raw, "resetPreferenceDocument");
    return mapMutationResultFromDTO(data.resetPreferenceDocument);
  }
  // -------------------------------------------------------------------------
  // TransportAdapter — subscriptions (v2 — not yet implemented)
  // -------------------------------------------------------------------------
  /**
   * Subscriptions are not supported in v1.
   *
   * Real-time preference change delivery requires a dedicated subscription
   * executor (e.g. a WebSocket link) that is distinct from the standard
   * query/mutation executor. Use `createSubscribableAdapter()` (coming in v2)
   * to obtain an adapter that supports subscriptions.
   *
   * @throws {Error} Always. This method is intentionally unimplemented in v1.
   */
  subscribe(_input, _callback) {
    throw new Error(
      "GraphQLTransportAdapter: subscriptions require a dedicated subscription executor. Use createSubscribableAdapter() for subscription support."
    );
  }
};

export {
  BOOTSTRAP_QUERY,
  PREFERENCE_DOCUMENT_QUERY,
  EFFECTIVE_PREFERENCES_QUERY,
  PATCH_PREFERENCE_DOCUMENT_MUTATION,
  RESET_PREFERENCE_DOCUMENT_MUTATION,
  PREFERENCE_EVENTS_SUBSCRIPTION,
  GraphQLTransportAdapter
};
//# sourceMappingURL=chunk-IG424G2L.js.map